import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getPartner,
  updatePartner,
  addPartnerToSite,
  removePartnerFromSite,
  createPartnerSitePrice,
  deletePartnerSitePrice,
  addPartnerThirdPartySystemMapping,
  deletePartnerThirdPartySystemMapping,
} from "../../api/Partners";
import { getAllChargingSites, getAllChargingSitesForPartner } from "../../api/ChargingSites";

import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import LoadingSpinner from "../../components/LoadingSpinner";
import PartnerForm from "../../components/partner/PartnerForm";
import EntityMultiSelectForm from "../../components/EntityMultiSelectForm";
import SitesPriceTable from "../../components/partner/SitesPriceTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import PartnerThirdPartyRef from "../../components/partner/PartnerThirdPartyRef";

const EditPartnerPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [activeTab, setActiveTab] = useState(query.get("tab") ? query.get("tab") : "details");
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  const [loadingSites, setLoadingSites] = useState(true);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [originalSiteIds, setOriginalSiteIds] = useState([]);
  const [allSitesById, setAllSitesById] = useState({});

  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });

  const [addSitePriceModal, setAddSitePriceModal] = useState(false);
  const [addSitePriceState, setAddSitePriceState] = useState([]);
  const [sitePrices, setSitePrices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [thirdPartySystemMapping, setThirdPartySystemMapping] = useState([]);

  const [partner, setPartner] = useState({
    name: "",
    organizationNumber: "",
    email: "",
    address: "",
    zipCode: "",
    city: "",
    country: "",
    phonePrefix: "",
    phone: "",
  });

  const sites = useMemo(
    () =>
      selectedSiteIds
        .map((siteId) => allSitesById[siteId])
        .filter(Boolean),
    [selectedSiteIds, allSitesById]
  );

  const getSitePriceKey = (sitePrice) =>
    [
      sitePrice?.priceCategory ?? "",
      sitePrice?.priceValueType ?? "",
      String(sitePrice?.priceValue ?? ""),
    ].join("|");

  useEffect(() => {
    const tab = query.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [location.search]);

  useEffect(() => {
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.set("tab", activeTab);
    navigate(`${location.pathname}?${nextQuery.toString()}`, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [activeTab, location.pathname, navigate]);

  const handleInputChange = (key, value) => {
    setHasChanges(true);
    setPartner((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    handleGetChargingPartner();
    handleGetSitesForPartner();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const mergeSitesById = (list) => {
    setAllSitesById((prev) => {
      const next = { ...prev };
      (list || []).forEach((site) => {
        const siteId = site?.chargingSitePublicId;
        if (siteId) {
          next[siteId] = site;
        }
      });
      return next;
    });
  };

  const handleGetSitesForPartner = async () => {
    setLoadingSites(true);
    try {
      const accessToken = await getAccessToken();
      const csites = await getAllChargingSitesForPartner(id, accessToken);
      const siteIds = (csites || [])
        .map((site) => site.chargingSitePublicId)
        .filter(Boolean);

      mergeSitesById(csites);
      setSelectedSiteIds(siteIds);
      setOriginalSiteIds(siteIds);
    } catch (error) {
      toast.error(error?.message || t("toast.failedLoad", { entity: t("entity.sites") }));
    } finally {
      setLoadingSites(false);
    }
  };

  const handleGetChargingPartner = async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const newPartner = await getPartner(id, accessToken);
      setPartner(newPartner);
    } catch (error) {
      toast.error(error?.message || t("toast.failedLoad", { entity: t("entity.partner") }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePartner = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const toastId = toast.loading(t("toast.updating", { entity: t("entity.partner") }));

    try {
      const accessToken = await getAccessToken();
      const newPartner = await updatePartner(partner, accessToken);
      setPartner(newPartner);

      const sitesToAdd = selectedSiteIds.filter((siteId) => !originalSiteIds.includes(siteId));
      const sitesToRemove = originalSiteIds.filter((siteId) => !selectedSiteIds.includes(siteId));

      const sitesUpdate = [
        ...sitesToAdd.map((siteId) => addPartnerToSite(id, siteId, accessToken)),
        ...sitesToRemove.map((siteId) => removePartnerFromSite(id, siteId, accessToken)),
      ];

      const thirdPartySystemPromises = thirdPartySystemMapping.map(async (thirdPartySystem) => {
        if (thirdPartySystem.isNew) {
          thirdPartySystem.partnerPublicId = newPartner.partnerPublicId;
          return addPartnerThirdPartySystemMapping(thirdPartySystem, accessToken);
        }

        if (thirdPartySystem.isDeleted) {
          return deletePartnerThirdPartySystemMapping(
            thirdPartySystem.partnerPublicId,
            thirdPartySystem.thirdPartySystemPublicId,
            thirdPartySystem.thirdPartySystemExternalId,
            accessToken
          );
        }

        return null;
      });

      const priceTasks = [];
      const seenSitePriceKeys = new Set();

      sitePrices.forEach((site) => {
        site.sitePrices.forEach((sitePrice) => {
          const priceKey = `${site.site.chargingSitePublicId}|${getSitePriceKey(sitePrice)}`;

          if (sitePrice.isNew) {
            if (seenSitePriceKeys.has(priceKey)) {
              return;
            }

            seenSitePriceKeys.add(priceKey);
            priceTasks.push(
              createPartnerSitePrice(
                {
                  chargingSitePublicId: site.site.chargingSitePublicId,
                  priceValue: sitePrice.priceValue,
                  priceValueType: sitePrice.priceValueType,
                  priceCategory: sitePrice.priceCategory,
                },
                accessToken
              )
            );
          } else if (sitePrice.isDeleted) {
            priceTasks.push(
              deletePartnerSitePrice(sitePrice.partnerChargingSitePricePublicId, accessToken)
            );
          }
        });
      });

      await Promise.all([...sitesUpdate, ...priceTasks, ...thirdPartySystemPromises]);
      setOriginalSiteIds([...selectedSiteIds]);
      setHasChanges(false);
      toast.success(t("toast.updated", { entity: t("entity.partner") }), { id: toastId });
    } catch (error) {
      toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.partner") }), {
        id: toastId,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const navigateBack = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: t("confirm.discard"),
        confirm: () => {
          if (navigate.length > 1) {
            navigate(-1);
          } else {
            navigate("/partners");
          }
        },
      }));
    } else if (navigate.length > 1) {
      navigate(-1);
    } else {
      navigate("/partners");
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("pages.editPartner")} loading={loading} />
          </div>

          <div className={`${loading ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button
                onClick={() => navigateBack()}
                className="text-black flex"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">{partner.name}</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigateBack()}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges || isSaving}
                  onClick={() => {
                    handleUpdatePartner();
                  }}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className="px-4 py-8 shadow-lg rounded-md w-full">
              <div className="border-b border-mute-200 mb-4 gap-4 flex">
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "details"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("details")}
                >
                  {t("common.details")}
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "sites"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("sites")}
                >
                  {t("tabs.sites")}
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "sitePrice"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("sitePrice")}
                >
                  {t("tabs.sitePrice")}
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "references"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("references")}
                >
                  {t("tabs.references")}
                </button>
              </div>

              <div className="flex flex-col w-full gap-4">
                {activeTab === "details" && (
                  <PartnerForm partner={partner} handleInputChange={handleInputChange} />
                )}

                {activeTab === "sites" &&
                  (loadingSites ? (
                    <LoadingSpinner text={t("common.loadingEntity", { name: t("entity.sites") })} />
                  ) : (
                    <EntityMultiSelectForm
                      title={t("entity.sites")}
                      description={t("partner.selectSites")}
                      fetchItems={async (token) => {
                        const list = await getAllChargingSites(token);
                        return (list || []).map((site) => ({
                          ...site,
                          chargingSitePublicId: site.chargingSitePublicId,
                          chargingSiteName: site.chargingSiteName || site.name || "",
                        }));
                      }}
                      labelKey="chargingSiteName"
                      valueKey="chargingSitePublicId"
                      selectedIds={selectedSiteIds}
                      onChange={(ids) => {
                        setSelectedSiteIds((ids || []).filter(Boolean));
                        setHasChanges(true);
                      }}
                      onItemsLoaded={mergeSitesById}
                    />
                  ))}

                {activeTab === "sitePrice" && (
                  <div className="flex w-full">
                    <SitesPriceTable
                      addModal={addSitePriceModal}
                      setAddModal={setAddSitePriceModal}
                      setAddSitePriceState={setAddSitePriceState}
                      addSitePriceState={addSitePriceState}
                      sites={sites}
                      sitePrices={sitePrices}
                      setSitePrices={setSitePrices}
                      setConfirmState={setConfirmState}
                      setHasChanges={setHasChanges}
                    />
                  </div>
                )}

                {activeTab === "references" && (
                  <PartnerThirdPartyRef
                    hasChanges={hasChanges}
                    thirdPartySystemMapping={thirdPartySystemMapping}
                    setThirdPartySystemMapping={setThirdPartySystemMapping}
                    setHasChanges={setHasChanges}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
          onConfirm={() => {
            setConfirmState({ ...confirmState, isOpen: false });
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditPartnerPage;
